import { useEffect, useMemo, useRef, useState } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { FolderArchive, Loader2, Upload, CheckCircle2, AlertTriangle, X, Sparkles, History, CloudUpload } from "lucide-react";
import { toast } from "sonner";
import { formatBytes, CATEGORY_LABELS, CATEGORY_ORDER } from "../lib/documents";
import { CategoryIcon } from "../lib/fileIcons";

const CHUNK = 5 * 1024 * 1024;
const AI_CATS = ["visita_medica", "certificato", "documento_identita"];
const AI_EXTS = ["pdf", "jpg", "jpeg", "png", "webp"];

const canAi = (f) => AI_EXTS.includes((f.filename.split(".").pop() || "").toLowerCase());

export const ZipImportCard = ({ employees, onImported }) => {
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadPct, setUploadPct] = useState(0);
    const [preview, setPreview] = useState(null);
    const [assignments, setAssignments] = useState({});
    const [overrides, setOverrides] = useState({});
    const [importing, setImporting] = useState(false);
    const [notifyEmails, setNotifyEmails] = useState(true);
    const [jobProgress, setJobProgress] = useState(null);
    const [result, setResult] = useState(null);
    const [aiBusyPath, setAiBusyPath] = useState(null);
    const [aiAllProgress, setAiAllProgress] = useState(null);
    const [savedAt, setSavedAt] = useState(null);
    const [restored, setRestored] = useState(false);
    const [dupFlags, setDupFlags] = useState({});
    const inputRef = useRef(null);
    const saveTimer = useRef(null);

    const initStateFromFolders = (folders, draft) => {
        const init = {};
        const ov = {};
        const flags = {};
        folders.forEach((f2) => {
            init[f2.folder] = f2.employee_id || "";
            f2.files.forEach((x) => {
                flags[x.path] = !!x.already_archived;
                ov[x.path] = { category: x.category, expires_at: "", title: x.title || "", skip: !x.supported || !!x.already_archived };
            });
        });
        const d = draft || {};
        setAssignments({ ...init, ...(d.assignments || {}) });
        const merged = { ...ov };
        Object.entries(d.file_overrides || {}).forEach(([p, o]) => {
            if (merged[p]) merged[p] = { ...merged[p], ...o };
        });
        Object.entries(flags).forEach(([p, dup]) => {
            if (dup && merged[p]) merged[p] = { ...merged[p], skip: true };
        });
        setOverrides(merged);
        setDupFlags(flags);
    };

    // Resume a pending import (server-side draft) on mount
    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/documents/import-zip/pending");
                if (!data.found) return;
                setPreview(data);
                initStateFromFolders(data.folders, data.draft);
                setSavedAt(data.draft_saved_at || null);
                setRestored(true);
            } catch (e) {
                // no pending import
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Autosave draft (debounced) whenever the user edits the preview
    useEffect(() => {
        if (!preview || result || importing) return;
        clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(async () => {
            try {
                const { data } = await api.patch(`/documents/import-zip/${preview.zip_id}/draft`, {
                    assignments,
                    file_overrides: overrides,
                });
                setSavedAt(data.saved_at);
            } catch (e) {
                // silent: will retry on next change
            }
        }, 1200);
        return () => clearTimeout(saveTimer.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assignments, overrides]);

    const sortedEmployees = useMemo(
        () => [...(employees || [])].sort((a, b) => `${a.cognome} ${a.nome}`.localeCompare(`${b.cognome} ${b.nome}`)),
        [employees],
    );

    const reset = async () => {
        if (preview && !result) {
            try {
                await api.delete(`/documents/import-zip/${preview.zip_id}`);
            } catch (e) {
                // already gone
            }
        }
        setFile(null);
        setPreview(null);
        setAssignments({});
        setOverrides({});
        setResult(null);
        setUploadPct(0);
        setJobProgress(null);
        setAiAllProgress(null);
        setSavedAt(null);
        setRestored(false);
        if (inputRef.current) inputRef.current.value = "";
    };

    const handleFile = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (!f.name.toLowerCase().endsWith(".zip")) {
            toast.error("Seleziona un file .zip");
            return;
        }
        setFile(f);
        setPreview(null);
        setResult(null);
    };

    const uploadZip = async () => {
        if (!file) return;
        setUploading(true);
        setUploadPct(0);
        try {
            const uploadId = crypto.randomUUID();
            const totalChunks = Math.max(1, Math.ceil(file.size / CHUNK));
            for (let i = 0; i < totalChunks; i++) {
                const fd = new FormData();
                fd.append("upload_id", uploadId);
                fd.append("chunk_index", String(i));
                fd.append("total_chunks", String(totalChunks));
                fd.append("filename", file.name);
                fd.append("file", file.slice(i * CHUNK, (i + 1) * CHUNK), "chunk.bin");
                const { data } = await api.post("/documents/import-zip/upload-chunk", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                    timeout: 300000,
                });
                setUploadPct(Math.round(((i + 1) / totalChunks) * 100));
                if (data.done) {
                    setPreview(data);
                    initStateFromFolders(data.folders, null);
                    setRestored(false);
                    toast.success(`Archivio analizzato: ${data.total_files} file in ${data.total_folders} cartelle (${data.matched_folders} abbinate)`);
                }
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore caricamento ZIP");
        } finally {
            setUploading(false);
        }
    };

    const setOv = (path, patch) =>
        setOverrides((prev) => ({ ...prev, [path]: { ...prev[path], ...patch } }));

    const recheckDuplicates = async (folderName, employeeId) => {
        if (!preview) return;
        try {
            const { data } = await api.post(`/documents/import-zip/${preview.zip_id}/check-duplicates`, {
                folder: folderName,
                employee_id: employeeId || "",
            });
            const folderObj = preview.folders.find((f) => f.folder === folderName);
            setDupFlags((prev) => ({ ...prev, ...data.flags }));
            setOverrides((prev) => {
                const next = { ...prev };
                Object.entries(data.flags).forEach(([p, dup]) => {
                    const fileObj = folderObj?.files.find((x) => x.path === p);
                    if (next[p]) next[p] = { ...next[p], skip: dup ? true : !(fileObj?.supported) };
                });
                return next;
            });
            const n = Object.values(data.flags).filter(Boolean).length;
            if (n > 0) toast.info(`${n} file già in archivio per questo dipendente: verranno saltati`);
        } catch (e) {
            // non-blocking: import-time check still applies
        }
    };

    const detectOne = async (f) => {
        setAiBusyPath(f.path);
        try {
            const { data } = await api.post(`/documents/import-zip/${preview.zip_id}/detect-expiry`, { path: f.path }, { timeout: 120000 });
            if (data.found) {
                setOv(f.path, { expires_at: data.expires_at });
                toast.success(`${f.filename}: scadenza ${data.expires_at.split("-").reverse().join("/")}`);
            } else {
                toast.info(`${f.filename}: nessuna scadenza trovata nel file`);
            }
            return data.found;
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore analisi AI");
            return false;
        } finally {
            setAiBusyPath(null);
        }
    };

    const detectAll = async () => {
        const eligible = [];
        preview.folders.forEach((fo) => {
            if (!assignments[fo.folder]) return;
            fo.files.forEach((x) => {
                const ov = overrides[x.path] || {};
                const cat = ov.category || x.category;
                if (!ov.skip && x.supported && canAi(x) && AI_CATS.includes(cat) && !ov.expires_at) eligible.push(x);
            });
        });
        if (!eligible.length) {
            toast.info("Nessun file idoneo per l'analisi AI (visite, certificati, documenti identità senza scadenza)");
            return;
        }
        setAiAllProgress({ done: 0, total: eligible.length });
        for (let i = 0; i < eligible.length; i++) {
            await detectOne(eligible[i]);
            setAiAllProgress({ done: i + 1, total: eligible.length });
        }
        setAiAllProgress(null);
        toast.success("Analisi AI completata: controlla le date rilevate");
    };

    const importCount = () =>
        preview
            ? preview.folders
                  .filter((f) => assignments[f.folder])
                  .reduce((n, f) => n + f.files.filter((x) => x.supported && !(overrides[x.path] || {}).skip).length, 0)
            : 0;

    const runImport = async () => {
        if (!preview) return;
        if (!importCount()) {
            toast.error("Nessun file da importare: assegna le cartelle e seleziona i file");
            return;
        }
        setImporting(true);
        setResult(null);
        try {
            const { data } = await api.post(`/documents/import-zip/${preview.zip_id}/apply`, {
                assignments,
                file_overrides: overrides,
                notify_employees: notifyEmails,
            });
            let job = data;
            for (let i = 0; i < 900 && job.status === "running"; i++) {
                await new Promise((r) => setTimeout(r, 2000));
                job = (await api.get(`/documents/import-zip/jobs/${data.job_id}`)).data;
                setJobProgress(job);
            }
            if (job.status === "error") {
                toast.error(job.error || "Errore durante l'import");
            } else {
                setResult(job);
                toast.success(`Import completato: ${job.created} documenti importati`);
                onImported && onImported();
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore import");
        } finally {
            setImporting(false);
            setJobProgress(null);
        }
    };

    return (
        <div className="mb-8 rounded-lg border border-slate-200 bg-white p-6 shadow-sm" data-testid="zip-import-card">
            <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-md bg-sky-100 text-sky-700">
                        <FolderArchive className="h-5 w-5" />
                    </div>
                    <div>
                        <h2 className="font-display text-base font-semibold text-slate-800">Importa ZIP (cartelle OneDrive)</h2>
                        <p className="text-xs text-slate-500">
                            Su OneDrive seleziona le cartelle dei dipendenti → Scarica → carica qui lo ZIP.
                            Prima dell'import controlli e correggi <b>categoria e scadenza di ogni file</b>.
                        </p>
                    </div>
                </div>
                {(preview || result) && (
                    <Button variant="ghost" size="icon" onClick={reset} title="Annulla e ricomincia (elimina la bozza)" data-testid="zip-reset-btn">
                        <X className="h-4 w-4" />
                    </Button>
                )}
            </div>

            {restored && preview && !result && (
                <div className="mt-3 flex items-center gap-2 rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-800" data-testid="zip-restored-banner">
                    <History className="h-4 w-4 flex-shrink-0" />
                    <span>
                        <b>Lavoro ripristinato</b> — archivio <b>{preview.original_name || "ZIP"}</b>: le tue selezioni, titoli e scadenze sono state recuperate
                        {savedAt ? ` (ultimo salvataggio ${new Date(savedAt).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })})` : ""}.
                    </span>
                </div>
            )}

            {!preview && !result && (
                <div className="mt-4 flex flex-wrap items-center gap-3">
                    <input
                        ref={inputRef}
                        type="file"
                        accept=".zip"
                        onChange={handleFile}
                        className="text-sm text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-sky-50 file:px-3 file:py-2 file:text-sm file:font-semibold file:text-sky-700 hover:file:bg-sky-100"
                        data-testid="zip-file-input"
                    />
                    <Button
                        onClick={uploadZip}
                        disabled={!file || uploading}
                        className="gap-2 bg-sky-600 text-white hover:bg-sky-700"
                        data-testid="zip-upload-btn"
                    >
                        {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                        {uploading ? `Caricamento ${uploadPct}%…` : "Carica e analizza"}
                    </Button>
                    {file && !uploading && (
                        <span className="text-xs text-slate-500">{file.name} · {formatBytes(file.size)}</span>
                    )}
                </div>
            )}

            {uploading && (
                <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-100">
                    <div className="h-full rounded-full bg-sky-500 transition-[width]" style={{ width: `${uploadPct}%` }} />
                </div>
            )}

            {preview && !result && (
                <div className="mt-4" data-testid="zip-preview">
                    <div className="mb-3 flex flex-wrap items-center gap-2 text-xs">
                        <span className="rounded-full bg-slate-100 px-2.5 py-1 font-semibold text-slate-700">{preview.total_folders} cartelle</span>
                        <span className="rounded-full bg-emerald-100 px-2.5 py-1 font-semibold text-emerald-700">{preview.matched_folders} abbinate</span>
                        <span className="rounded-full bg-slate-100 px-2.5 py-1 font-semibold text-slate-700">{preview.total_files} file</span>
                        {Object.values(dupFlags).filter(Boolean).length > 0 && (
                            <span className="rounded-full bg-sky-100 px-2.5 py-1 font-semibold text-sky-700" data-testid="zip-dup-count">
                                {Object.values(dupFlags).filter(Boolean).length} già in archivio
                            </span>
                        )}
                        <span className="flex items-center gap-1 text-[11px] text-slate-400" data-testid="zip-draft-saved" title="Le modifiche vengono salvate automaticamente sul server: puoi chiudere e riprendere quando vuoi">
                            <CloudUpload className="h-3.5 w-3.5" />
                            {savedAt ? `Bozza salvata ${new Date(savedAt).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}` : "Salvataggio automatico attivo"}
                        </span>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={detectAll}
                            disabled={!!aiAllProgress || !!aiBusyPath}
                            className="ml-auto h-7 gap-1.5 border-violet-200 bg-violet-50 px-2.5 text-xs text-violet-700 hover:bg-violet-100"
                            data-testid="zip-ai-all-btn"
                        >
                            {aiAllProgress ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                            {aiAllProgress ? `Analisi ${aiAllProgress.done}/${aiAllProgress.total}…` : "Rileva scadenze con AI"}
                        </Button>
                    </div>
                    <div className="max-h-[520px] space-y-3 overflow-y-auto rounded-md border border-slate-100 p-2">
                        {preview.folders.map((f) => {
                            const assigned = assignments[f.folder];
                            return (
                                <div
                                    key={f.folder || "(root)"}
                                    className={`rounded-md border ${assigned ? "border-emerald-100" : "border-amber-200"}`}
                                    data-testid={`zip-folder-${(f.folder || "root").replace(/\s+/g, "-")}`}
                                >
                                    <div className={`flex flex-wrap items-center gap-2 rounded-t-md px-3 py-2 ${assigned ? "bg-emerald-50/60" : "bg-amber-50/70"}`}>
                                        <span className="truncate text-sm font-semibold text-slate-700">📁 {f.folder || "(file senza cartella)"}</span>
                                        {f.employee_id && assigned === f.employee_id && (
                                            <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-emerald-700">auto</span>
                                        )}
                                        <select
                                            value={assigned || ""}
                                            onChange={(e) => {
                                                const v = e.target.value;
                                                setAssignments((prev) => ({ ...prev, [f.folder]: v }));
                                                recheckDuplicates(f.folder, v);
                                            }}
                                            className="ml-auto h-8 max-w-[240px] rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-700"
                                            data-testid={`zip-assign-${(f.folder || "root").replace(/\s+/g, "-")}`}
                                        >
                                            <option value="">— Non importare —</option>
                                            {sortedEmployees.map((e) => (
                                                <option key={e.id} value={e.id}>
                                                    {e.cognome} {e.nome} ({e.matricola}){e.data_cessazione ? " · cessato" : ""}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="divide-y divide-slate-100">
                                        {f.files.map((x) => {
                                            const ov = overrides[x.path] || {};
                                            const isDup = !!dupFlags[x.path];
                                            const excluded = !!ov.skip || !x.supported;
                                            return (
                                                <div
                                                    key={x.path}
                                                    className={`px-3 py-1.5 ${excluded ? "opacity-50" : ""}`}
                                                    data-testid={`zip-file-row`}
                                                >
                                                    <div className="flex flex-wrap items-center gap-2">
                                                        <input
                                                            type="checkbox"
                                                            checked={!excluded}
                                                            disabled={!x.supported || isDup}
                                                            onChange={(e) => setOv(x.path, { skip: !e.target.checked })}
                                                            title={isDup ? "Già presente in archivio: verrà saltato" : x.supported ? "Includi/escludi dall'import" : "Formato non supportato"}
                                                            data-testid="zip-file-include"
                                                        />
                                                        <CategoryIcon category={ov.category || x.category} label={CATEGORY_LABELS[ov.category || x.category]} />
                                                        <input
                                                            type="text"
                                                            value={ov.title ?? ""}
                                                            onChange={(e) => setOv(x.path, { title: e.target.value })}
                                                            disabled={excluded}
                                                            placeholder="Titolo del documento"
                                                            className="h-7 min-w-[180px] flex-1 rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-700"
                                                            title="Titolo (generato automaticamente, modificabile)"
                                                            data-testid="zip-file-title"
                                                        />
                                                        <select
                                                            value={ov.category || x.category}
                                                            onChange={(e) => setOv(x.path, { category: e.target.value })}
                                                            disabled={excluded}
                                                            className="h-7 rounded-md border border-slate-200 bg-white px-1.5 text-[11px] text-slate-700"
                                                            data-testid="zip-file-category"
                                                        >
                                                            {CATEGORY_ORDER.map((c) => (
                                                                <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>
                                                            ))}
                                                        </select>
                                                        <input
                                                            type="date"
                                                            value={ov.expires_at || ""}
                                                            onChange={(e) => setOv(x.path, { expires_at: e.target.value })}
                                                            disabled={excluded}
                                                            className="h-7 rounded-md border border-slate-200 bg-white px-1.5 text-[11px] text-slate-600"
                                                            title="Scadenza (opzionale)"
                                                            data-testid="zip-file-expiry"
                                                        />
                                                        <button
                                                            type="button"
                                                            onClick={() => detectOne(x)}
                                                            disabled={excluded || !canAi(x) || !!aiBusyPath || !!aiAllProgress}
                                                            className="flex h-7 w-7 items-center justify-center rounded-md border border-violet-200 bg-violet-50 text-violet-600 hover:bg-violet-100 disabled:opacity-40"
                                                            title="Rileva scadenza dal file con AI"
                                                            data-testid="zip-file-ai-btn"
                                                        >
                                                            {aiBusyPath === x.path ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                                                        </button>
                                                    </div>
                                                    <div className="ml-[52px] truncate text-[10px] text-slate-400" title={x.filename}>
                                                        {x.filename} · {formatBytes(x.size)}
                                                        {!x.supported && <span className="ml-1.5 font-semibold text-red-500">non supportato</span>}
                                                        {isDup && <span className="ml-1.5 font-semibold text-sky-600" data-testid="zip-file-dup-badge">già in archivio — verrà saltato</span>}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <label className="mt-3 flex cursor-pointer items-start gap-2 text-sm text-slate-700" data-testid="zip-notify-wrap">
                        <input
                            type="checkbox"
                            checked={notifyEmails}
                            onChange={(e) => setNotifyEmails(e.target.checked)}
                            className="mt-0.5"
                            data-testid="zip-notify-checkbox"
                        />
                        <span>
                            <span className="font-medium text-slate-800">Invia email di notifica ai dipendenti al termine dell'import</span>
                            <span className="block text-xs text-slate-500">Ogni dipendente riceve l'email "nuovo documento disponibile" per i file importati</span>
                        </span>
                    </label>
                    <div className="mt-3 flex items-center gap-3">
                        <Button
                            onClick={runImport}
                            disabled={importing}
                            className="gap-2 bg-emerald-600 text-white hover:bg-emerald-700"
                            data-testid="zip-import-btn"
                        >
                            {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                            {importing
                                ? (jobProgress ? `Import ${jobProgress.processed}/${jobProgress.total}…` : "Avvio import…")
                                : `Importa ${importCount()} file`}
                        </Button>
                        <span className="text-xs text-slate-500">
                            Nulla viene importato finché non premi qui. I duplicati già presenti vengono saltati.
                        </span>
                    </div>
                </div>
            )}

            {result && (
                <div className="mt-4 rounded-md border border-emerald-200 bg-emerald-50 p-4" data-testid="zip-result">
                    <div className="flex items-center gap-2 font-semibold text-emerald-800">
                        <CheckCircle2 className="h-4 w-4" /> Import completato
                    </div>
                    <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-700">
                        <span><b>{result.created}</b> importati</span>
                        {result.duplicates > 0 && <span><b>{result.duplicates}</b> duplicati saltati</span>}
                        {result.skipped > 0 && <span><b>{result.skipped}</b> esclusi/non supportati</span>}
                        {result.errors > 0 && (
                            <span className="flex items-center gap-1 text-red-700"><AlertTriangle className="h-3.5 w-3.5" /><b>{result.errors}</b> errori</span>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};
